import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import SectionLabel from "./SectionLabel";

const STEPS = [
  { n: "01", title: "Concept", body: "Brand thinking + commercial intent." },
  { n: "02", title: "Design", body: "Identity, UI, environments." },
  { n: "03", title: "Build", body: "3D, fabrication, code." },
  { n: "04", title: "Execute", body: "Install, deploy, ship." },
  { n: "05", title: "Manage", body: "Maintain, scale, evolve." },
];

const TEAMS = [
  "3D design team",
  "Digital & AI content team",
  "Website & app developers",
  "Stall fabricators",
  "RCIP outlet builders",
  "Warehouse & logistics support",
  "Solution architects",
];

const ProcessPipeline = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });

  return (
    <section
      data-testid="process-pipeline"
      className="relative py-24 md:py-32 border-t-2 border-black"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <SectionLabel className="mb-6" testid="label-process">
              [ 05 ] - HOW WE WORK
            </SectionLabel>
            <h2
              className="font-archivo font-black h-tighter text-4xl md:text-5xl lg:text-6xl text-black"
              style={{ fontFamily: "Archivo, sans-serif" }}
            >
              Concept → Design → Build →<br />
              <span className="text-[#AE519F]">Execute → Manage.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className="text-black/70 text-base md:text-lg max-w-md"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              UPRCSE integrates multiple capabilities under one system. This
              ensures control, consistency and accountability at every stage.
            </p>
          </div>
        </div>

        {/* Pipeline */}
        <div ref={ref} className="relative">
          {/* spine line drawing */}
          <svg
            className="absolute left-0 right-0 top-[40px] w-full h-2 hidden md:block pointer-events-none"
            viewBox="0 0 1200 4"
            preserveAspectRatio="none"
            aria-hidden
          >
            <motion.line
              x1="20"
              x2="1180"
              y1="2"
              y2="2"
              stroke="#000000"
              strokeWidth="1.4"
              initial={{ pathLength: 0 }}
              animate={inView ? { pathLength: 1 } : { pathLength: 0 }}
              transition={{ duration: 1.4, ease: [0.22, 0.61, 0.36, 1] }}
              style={{ pathLength: 1 }}
            />
          </svg>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-black/30 relative">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: i * 0.1 }}
                viewport={{ once: true }}
                className="bg-[#CCD6DC] p-6 md:p-7 relative"
              >
                {/* Node */}
                <div className="hidden md:block absolute -top-[5px] left-7 w-2.5 h-2.5 rotate-45 border border-black bg-[#CCD6DC]" />
                <div className="mono-label mb-4">{`STEP / ${s.n}`}</div>
                <div
                  className="text-black text-2xl md:text-[26px] mb-2"
                  style={{
                    fontFamily: "Archivo, sans-serif",
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {s.title}
                </div>
                <div
                  className="text-black/65 text-sm leading-relaxed"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {s.body}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Teams that power it */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="mono-label mb-4">UNDER ONE ROOF</div>
            <h3
              className="text-black text-3xl md:text-4xl"
              style={{
                fontFamily: "Archivo, sans-serif",
                fontWeight: 800,
                letterSpacing: "-0.02em",
              }}
            >
              The teams that power the pipeline.
            </h3>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/30">
              {TEAMS.map((t, i) => (
                <motion.div
                  key={t}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.45, delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-[#CCD6DC] p-5 flex items-center gap-4 group hover:bg-[#FFBBFF]/40 transition-colors"
                >
                  <span
                    className="mono-label text-black/60 group-hover:text-[#AE519F] transition-colors"
                  >
                    {`0${i + 1}`.slice(-2)}
                  </span>
                  <span
                    className="text-black text-base"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {t}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessPipeline;
